import { useEffect, useState } from 'react'
import { usePlatform } from '../platform'
import { useSignIn } from '../auth'
import { Icon, Logo } from './Icon'
import { Spinner } from './common'

export function SignIn({ initialError }: { initialError?: string | null }) {
  const platform = usePlatform()
  const { start, withToken, busy, error } = useSignIn()
  const [showToken, setShowToken] = useState(!platform.canOAuth)
  const [token, setToken] = useState('')
  const shownError = error ?? initialError
  useEffect(() => platform.setChrome?.('dark'), [platform])

  return (
    <main className="signin">
      <div className="signin-card">
        <div className="signin-logo">
          <Logo size={56} />
        </div>
        <h1>Assembly</h1>
        <p className="signin-tagline">All of Slack, in a calmer place.</p>

        {platform.canOAuth && (
          <button type="button" className="signin-button" onClick={start} disabled={busy}>
            {busy ? <Spinner size={18} /> : <SlackGlyph />}
            <span>Sign in with Slack</span>
          </button>
        )}

        {shownError && <p className="signin-error">{shownError.replace(/_/g, ' ')}</p>}

        {platform.canOAuth && !showToken && (
          <button type="button" className="signin-link" onClick={() => setShowToken(true)}>
            Use a user token instead
          </button>
        )}

        {showToken && (
          <form
            className="signin-token"
            onSubmit={(e) => {
              e.preventDefault()
              withToken(token)
            }}
          >
            <label htmlFor="token">Slack user token</label>
            <input id="token" type="password" autoComplete="off" placeholder="xoxp-…" value={token} onChange={(e) => setToken(e.target.value)} />
            <button type="submit" className="signin-button secondary" disabled={!token || busy}>
              {busy ? <Spinner size={16} /> : <Icon name="arrowUp" size={16} />}
              <span>Continue</span>
            </button>
            <small>Create a Slack app with the scopes in slack-manifest.yml, install it, and paste its User OAuth Token.</small>
          </form>
        )}
      </div>
      <GetTheApps />
      <footer className="signin-footer">Assembly isn’t affiliated with Slack Technologies.</footer>
    </main>
  )
}

function SlackGlyph() {
  return (
    <span className="slack-glyph">
      <Logo size={18} />
    </span>
  )
}

const isIos = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const isIosSafari = () => isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(navigator.userAgent)
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true

/** Download / install links for the native apps, shown on the web build only. */
function GetTheApps() {
  const platform = usePlatform()
  const [showSteps, setShowSteps] = useState(false)
  const [copied, setCopied] = useState(false)
  if (platform.kind !== 'web' || isStandalone()) return null
  const { mac, ios } = platform.downloads ?? { mac: null, ios: null }
  const onIos = isIos()
  const url = window.location.origin

  return (
    <section className="get-apps" aria-label="Get the apps">
      <div className="get-apps-buttons">
        {!onIos &&
          (mac ? (
            <a className="app-button" href={mac} download>
              <Icon name="laptop" size={20} />
              <span>
                <small>Download for</small>
                <strong>macOS</strong>
              </span>
            </a>
          ) : (
            <span className="app-button unavailable" title="The macOS app hasn’t been published yet">
              <Icon name="laptop" size={20} />
              <span>
                <small>Coming soon</small>
                <strong>macOS</strong>
              </span>
            </span>
          ))}
        {ios ? (
          <a className="app-button" href={ios} target="_blank" rel="noreferrer">
            <Icon name="phone" size={20} />
            <span>
              <small>Install on</small>
              <strong>iPhone</strong>
            </span>
          </a>
        ) : (
          <button type="button" className={`app-button ${showSteps ? 'active' : ''}`} onClick={() => setShowSteps(!showSteps)} aria-expanded={showSteps}>
            <Icon name="phone" size={20} />
            <span>
              <small>Install on</small>
              <strong>iPhone</strong>
            </span>
          </button>
        )}
      </div>

      {showSteps && (
        <div className="install-steps">
          {onIos && isIosSafari() ? (
            <ol>
              <li>
                Tap <Icon name="share" size={15} /> <strong>Share</strong> in Safari’s toolbar
              </li>
              <li>
                Choose <Icon name="plusSquare" size={15} /> <strong>Add to Home Screen</strong>
              </li>
              <li>
                Tap <strong>Add</strong>, then open Assembly from your Home Screen
              </li>
            </ol>
          ) : onIos ? (
            <p>Open this page in <strong>Safari</strong>, then tap Share → Add to Home Screen.</p>
          ) : (
            <>
              <p>
                On your iPhone, open this address in <strong>Safari</strong>, then tap Share → <strong>Add to Home Screen</strong>:
              </p>
              <button
                type="button"
                className="install-url"
                onClick={() => {
                  navigator.clipboard?.writeText(url).then(() => setCopied(true))
                  setTimeout(() => setCopied(false), 2000)
                }}
              >
                <span>{url.replace(/^https?:\/\//, '')}</span>
                <Icon name={copied ? 'check' : 'copy'} size={14} />
              </button>
            </>
          )}
        </div>
      )}
    </section>
  )
}
